import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

const HEADER_HEIGHT = 42;

export default function ImageViewer({
    imgSource,
    title,
    initialPosition,
    onClose,
}) {
    const [imageSize, setImageSize] = useState({
        width: 600,
        height: 400,
    });

    const [position, setPosition] = useState(
        initialPosition ?? { x: 200, y: 150 }
    );

    const [isDragging, setIsDragging] = useState(false);

    const dragOffset = useRef({ x: 0, y: 0 });
    const windowRef = useRef(null);

    // ============================================
    // LOAD IMAGE + CALCULATE ASPECT RATIO
    // ============================================

    useEffect(() => {
        if (!imgSource) return;

        const img = new Image();

        img.onload = () => {
            const aspectRatio = img.naturalWidth / img.naturalHeight;

            // Maximum available size
            const maxWidth = window.innerWidth * 0.8;
            const maxHeight = window.innerHeight * 0.7;

            let width;
            let height;

            // Fit image inside available area
            if (aspectRatio > maxWidth / maxHeight) {
                // Landscape / wide image
                width = maxWidth;
                height = width / aspectRatio;
            } else {
                // Portrait / tall image
                height = maxHeight;
                width = height * aspectRatio;
            }

            setImageSize({
                width: Math.round(width),
                height: Math.round(height),
            });

            // Center window
            if (!initialPosition) {
                setPosition({
                    x: Math.max(10, (window.innerWidth - width) / 2),
                    y: Math.max(
                        10,
                        (window.innerHeight - height - HEADER_HEIGHT) / 2
                    ),
                });
            }
        };

        img.src = imgSource;
    }, [imgSource, initialPosition]);

    // ============================================
    // HANDLE WINDOW RESIZE
    // ============================================

    useEffect(() => {
        const handleResize = () => {
            const img = new Image();

            img.onload = () => {
                const aspectRatio =
                    img.naturalWidth / img.naturalHeight;

                const maxWidth = window.innerWidth * 0.9;
                const maxHeight = window.innerHeight * 0.8;

                let width;
                let height;

                if (aspectRatio > maxWidth / maxHeight) {
                    width = maxWidth;
                    height = width / aspectRatio;
                } else {
                    height = maxHeight;
                    width = height * aspectRatio;
                }

                setImageSize({
                    width: Math.round(width),
                    height: Math.round(height),
                });
            };

            img.src = imgSource;
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [imgSource]);

    // ============================================
    // DRAG START
    // ============================================

    const handleMouseDown = useCallback((e) => {
        if (e.button !== 0) return;

        const rect = windowRef.current?.getBoundingClientRect();

        if (rect) {
            dragOffset.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        }

        setIsDragging(true);
    }, []);

    // ============================================
    // DRAG MOVE
    // ============================================

    const handleMouseMove = useCallback(
        (e) => {
            if (!isDragging) return;

            setPosition({
                x: e.clientX - dragOffset.current.x,
                y: e.clientY - dragOffset.current.y,
            });
        },
        [isDragging]
    );

    // ============================================
    // DRAG END
    // ============================================

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // ============================================
    // RENDER
    // ============================================

    return (
        <>
            {/* Background overlay */}

            <div
                className="fixed inset-0 z-[60]"
                style={{
                    backgroundColor: "rgba(0,0,0,0.07)",
                }}
                onClick={onClose}
            />

            {/* Image Window */}

            <motion.div
                ref={windowRef}
                className="fixed z-[70] overflow-hidden"
                initial={{
                    scale: 0.8,
                    opacity: 0,
                    y: 40,
                }}
                animate={{
                    scale: 1,
                    opacity: 1,
                    y: 0,
                }}
                exit={{
                    scale: 0.85,
                    opacity: 0,
                }}
                transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                }}
                style={{
                    width: imageSize.width,
                    height: imageSize.height + HEADER_HEIGHT,

                    left: position.x,
                    top: position.y,

                    borderRadius: 10,

                    boxShadow:
                        "0 10px 30px rgba(0,0,0,0.15)",

                    cursor: isDragging
                        ? "grabbing"
                        : "default",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ============================================
                    WINDOW HEADER
                ============================================ */}

                <div
                    className="
                        relative
                        flex
                        items-center
                        bg-slate-100/80
                        backdrop-blur-sm
                        px-4
                        pt-3
                        pb-2
                        cursor-grab
                    "
                    style={{
                        height: HEADER_HEIGHT,
                    }}
                    onMouseDown={handleMouseDown}
                >
                    {/* macOS buttons */}

                    <div className="flex items-center gap-2 z-10">

                        {/* Close */}

                        <button
                            onClick={onClose}
                            className="
                                w-3
                                h-3
                                rounded-full
                                flex
                                items-center
                                justify-center
                                group
                            "
                            style={{
                                backgroundColor: "#ff6157",
                                border: "0.5px solid #e24640",
                            }}
                        >
                            <svg
                                width="6"
                                height="6"
                                viewBox="0 0 6 6"
                                className="
                                    opacity-0
                                    group-hover:opacity-100
                                    transition-opacity
                                "
                            >
                                <path
                                    d="M0.5 0.5L5.5 5.5M5.5 0.5L0.5 5.5"
                                    stroke="#4c0002"
                                    strokeWidth="1"
                                />
                            </svg>
                        </button>

                        {/* Minimize */}

                        <div
                            className="
                                w-3
                                h-3
                                rounded-full
                                bg-[#ffbd2e]
                                border
                                border-[#dea123]"
                            
                        />

                        {/* Maximize */}

                        <div
                            className="
                                w-3
                                h-3
                                rounded-full
                                bg-[#28c940]
                                border
                                border-[#1bac2c]"
                            
                        />
                    </div>

                    {/* Title */}

                    <h1
                        className="
                            absolute
                            left-1/2
                            -translate-x-1/2
                            text-[13px]
                            font-bold
                            text-black/70
                            pointer-events-none
                            font-applefont
                            whitespace-nowrap
                            max-w-[70%]
                            overflow-hidden
                            text-ellipsis
                        "
                    >
                        {title}
                    </h1>
                </div>

                {/* ============================================
                    IMAGE
                ============================================ */}

                <div
                    className="
                        w-full
                        bg-white
                        flex
                        items-center
                        justify-center
                        overflow-hidden
                    "
                    style={{
                        height: imageSize.height,
                    }}
                    onContextMenu={(e)=> e.preventDefault()}
                    onDrag={(e)=>e.preventDefault()}
                    onSelect={(e)=>e.preventDefault()}
                >
                    <img
                        src={imgSource}
                        alt={title || "Image"}
                        className="
                            block
                            w-full
                            h-full
                            object-contain
                            select-none
                            pointer-events-none
                        "
                        draggable={false}
                    />
                </div>
            </motion.div>
        </>
    );
}